import { ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Monitor, Smartphone, Tablet } from 'lucide-react';

import { cn } from '@/lib/utils';
import { StatusBadge, StatusTone } from '../../../components/design-system/admin/StatusBadge';
import { Thumb, ThumbStack } from '../../../components/design-system/analytics/Thumb';
import type { ConversationOutcome, ShownProduct } from '../../../common/hooks/use-shopper-insights-api.hook';
import { useAskAi } from '../../../modules/vendor/assistant/AskAiContext';
import type { UnansweredReason } from './assistant-reading';

/**
 * The small pieces the Shopping Assistant pages share: how an outcome, a
 * theme and a reason read, the product thumbnails, the page header, and the
 * way to hand a question to Ardi.
 */

/** Page header: the 22px title, one line under it, and the page's controls at the end. */
export const AssistantHeader = ({ title, subtitle, actions }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode }) => (
    <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
            <h1 className="font-heading text-[22px] font-medium tracking-tight">{title}</h1>
            {subtitle && <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{subtitle}</p>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
);

const OUTCOME_TONE: Record<ConversationOutcome, StatusTone> = {
    unanswered: 'warning',
    room: 'success',
    recommended: 'success',
    answered: 'neutral',
};

export const useOutcomeLabel = () => {
    const { t } = useTranslation();
    return useCallback((outcome: ConversationOutcome) => t(`SHOPPER_UI.OUTCOME.${outcome.toUpperCase()}`), [t]);
};

/** How a conversation ended, as a dot and a word — amber only for what needs the vendor. */
export const OutcomeBadge = ({ outcome, className }: { outcome: ConversationOutcome; className?: string }) => {
    const label = useOutcomeLabel();
    return (
        <StatusBadge tone={OUTCOME_TONE[outcome]} className={className}>
            {label(outcome)}
        </StatusBadge>
    );
};

export const useThemeLabel = () => {
    const { t } = useTranslation();
    return useCallback(
        (theme: string) => t(`SHOPPER_UI.THEMES.${String(theme || 'other').toUpperCase()}`, { defaultValue: t('SHOPPER_UI.THEMES.OTHER') }),
        [t],
    );
};

export const useReasonLabel = () => {
    const { t } = useTranslation();
    return useCallback((reason: UnansweredReason) => t(`SHOPPER_UI.REASON.${reason.toUpperCase()}`), [t]);
};

export const useDeviceLabel = () => {
    const { t } = useTranslation();
    return useCallback(
        (device: string | null | undefined) =>
            t(`SHOPPER_UI.DEVICE.${String(device || 'unknown').toUpperCase()}`, { defaultValue: t('SHOPPER_UI.DEVICE.UNKNOWN') }),
        [t],
    );
};

export const DeviceIcon = ({ device, className }: { device: string | null | undefined; className?: string }) => {
    const Icon = device === 'mobile' ? Smartphone : device === 'tablet' ? Tablet : Monitor;
    return <Icon className={className} aria-hidden />;
};

/**
 * The products the assistant put in front of the shopper, as overlapping
 * thumbnails. The row carries four at most; `total` says how many there were,
 * so the "+N" counts the ones the API left out too.
 */
export const ProductThumbs = ({ products, total }: { products: ShownProduct[] | null | undefined; total?: number }) => {
    const list = products || [];
    if (!list.length) return null;
    const items = list.map((p, i) => ({ key: p.sku || p.uuid || String(i), src: p.image, alt: p.name || p.sku || '' }));
    // Placeholders for the ones not sent, so ThumbStack's "+N" is the real remainder.
    for (let i = list.length; i < (total ?? list.length); i++) items.push({ key: `more-${i}`, src: null, alt: '' });
    return <ThumbStack items={items} max={4} size="xs" />;
};

/** One product card under a reply, as the shopper saw it: picture, name, price. */
export const ProductCard = ({ product }: { product: { name?: string; sku?: string; image?: string; price?: string } }) => (
    <span className="flex w-40 shrink-0 items-center gap-2 rounded-lg border bg-background p-1.5">
        <Thumb src={product.image} alt={product.name || product.sku || ''} size="md" />
        <span className="min-w-0">
            <span className="line-clamp-2 block text-xs leading-snug">{product.name || product.sku}</span>
            {product.price && <span className="mt-0.5 block text-xs tabular-nums text-muted-foreground">{product.price}</span>}
        </span>
    </span>
);

/** A shopper, who has no name: a neutral circle with the device they wrote from. */
export const ShopperAvatar = ({ device, className }: { device: string | null | undefined; className?: string }) => (
    <span className={cn('flex size-9 shrink-0 items-center justify-center rounded-full border bg-muted', className)}>
        <DeviceIcon device={device} className="size-4 text-muted-foreground" />
    </span>
);

/**
 * Hand a question the assistant could not answer to Ardi, who can read the
 * catalogue and the conversation and say what is missing. Null when Ask AI is
 * not mounted (a test, a page outside the vendor shell).
 */
export const useAskArdiAbout = () => {
    const { t } = useTranslation();
    const askAi = useAskAi();
    const ask = useCallback(
        (question: string, sku?: string | null) => {
            if (!askAi) return;
            askAi.ask(
                sku
                    ? t('SHOPPER_UI.ASK_ARDI.PROMPT_SKU', { question, sku })
                    : t('SHOPPER_UI.ASK_ARDI.PROMPT', { question }),
            );
        },
        [askAi, t],
    );
    return askAi ? ask : null;
};
